import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  APP_VERSION,
  VERSION_POLL_MS,
  fetchDeployedVersion,
  readLastPrompt,
  updatePrompt,
  writeLastPrompt,
} from "@/lib/app-version";
import { RELEASE_NOTES, visibleReleases } from "@/lib/changelog/release-registry";
import { useVisibilityContext } from "@/hooks/use-changelog";

const BUNDLE_RELEASE_IDS: ReadonlySet<string> = new Set(RELEASE_NOTES.map((r) => r.id));

/**
 * VIB-2834: om fanen skal be brukeren laste inn Revehiet på nytt.
 * Sjekker når fanen får fokus og hvert femte minutt. Leser ingen tabell, så
 * queryKey-en hører ikke hjemme i CACHE_REGISTRY.
 *
 * Varsel med en gang bare når den nye versjonen har en utrulling brukeren får
 * se. Ellers høyst én gang per døgn (regelen bor i `updatePrompt`).
 */
export function useAppVersion() {
  const { data: deployed = null } = useQuery({
    queryKey: ["app-version"],
    queryFn: () => fetchDeployedVersion(),
    refetchInterval: VERSION_POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
    retry: false,
  });
  const { role, isFlagOn } = useVisibilityContext();
  const [lastPrompt, setLastPrompt] = useState(readLastPrompt);

  const prompt = useMemo(
    () =>
      updatePrompt({
        current: APP_VERSION,
        deployed,
        bundleReleaseIds: BUNDLE_RELEASE_IDS,
        visibleDeployed: deployed ? visibleReleases(deployed.releases, role, isFlagOn) : [],
        lastPrompt,
        now: Date.now(),
      }),
    [deployed, role, isFlagOn, lastPrompt],
  );

  const deployedVersion = deployed?.version ?? null;
  useEffect(() => {
    if (!prompt || !deployedVersion || lastPrompt?.version === deployedVersion) return;
    const shown = { version: deployedVersion, at: Date.now() };
    writeLastPrompt(shown);
    setLastPrompt(shown);
  }, [prompt, deployedVersion, lastPrompt]);

  return {
    currentVersion: APP_VERSION,
    deployedVersion,
    updateAvailable: prompt !== null,
    release: prompt?.release ?? null,
  };
}
